import { chain } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import VoxelAutocompleteDomainMulti from '../../../components/muiLegacy/Autocomplete/VoxelAutocompleteDomainMulti';
import VoxelAutocompleteTagGroup from '../../../components/muiLegacy/Autocomplete/VoxelAutocompleteTagGroup';
import VoxelAutocompleteTagMulti from '../../../components/muiLegacy/Autocomplete/VoxelAutocompleteTagMulti';
import { KeywordTableState } from '../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import VoxelDatePicker from '../../../components/voxel/Forms/VoxelDatePicker';
import VoxelSwitch from '../../../components/voxel/Forms/VoxelSwitch';
import { MenuItem } from '../../../components/voxel/Menu/VoxelMenu';
import VoxelToggleTabs from '../../../components/voxel/Tabs/VoxelToggleTabs';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { KeywordFilteringGq } from '../../../graphql/projects/models/KeywordFilteringGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { useWidthObserver } from '../../../logic/otherUtilities/useWidthObserver';
import RankTrackingSwitch from '../RankTrackingSwitch';
import SubscribeButton from '../SubscribeButton';
import ProgressDashboardCompetitors from './ProgressDashboardCompetitors/ProgressDashboardCompetitors';
import ProgressDashboardSite from './ProgressDashboardSite/ProgressDashboardSite';
import ProgressDashboardTags from './ProgressDashboardTags/ProgressDashboardTags';
import { ProgressDateSelectBag } from './useProgressDateSelect';

export interface ProgressDashboardProps {
  project: ProjectGq;
  filter: KeywordFilteringGq;
  pollInterval?: number;
  user: CurrentUserGq;
  reloadKeywords: () => Promise<any>;
  keywordTableState: KeywordTableState;
  dateSelectBag: ProgressDateSelectBag;
  setDashboardLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export type Dashboards = 'site' | 'competitors' | 'tags';

export const ProgressDashboard: React.FC<ProgressDashboardProps> = ({
  project,
  filter,
  pollInterval,
  user,
  reloadKeywords,
  dateSelectBag,
  setDashboardLoading,
}) => {
  const menuItems: MenuItem[] = [
    { id: 'site', name: 'Site', disabled: false },
    { id: 'competitors', name: 'Competitors', disabled: !!!project.domains.length },
    { id: 'tags', name: 'Tags', disabled: !!!project.tagGroups.length || !project.progressHasTags },
  ];

  const [selectedDashboard, setSelectedDashboard] = useState<Dashboards>('site');

  const [domainIds, setDomainIds] = useState<number[]>(
    chain(project.domains)
      .filter((d) => d.type !== DomainTypeGq.AUTOMATIC)
      .map((d) => d.id)
      .slice(0, 5)
      .value()
  );

  // Tag group
  const [tagGroupId, setTagGroupId] = useState<number>(
    chain(project.tagGroups)
      .filter((tg) => !!tg.tags.length)
      .orderBy((t) => t.id)
      .value()[0]?.id
  );
  const tagGroup = project.tagGroups.find((tg) => tg.id === tagGroupId);

  // Tag ids
  const initialTagIds = useMemo(() => {
    return chain(tagGroup?.tags)
      .orderBy((d) => d.id)
      .map((d) => d.id)
      .slice(0, 5)
      .value();
  }, [tagGroup?.tags, project]);

  const [tagIds, setTagIds] = useState<number[]>(initialTagIds);
  // Reset tag ids if the tag group is new
  useEffect(() => {
    setTagIds(initialTagIds);
  }, [tagGroupId]);

  // Use automatic domains switch
  const [useAutomaticDomainsFE, setUseAutomaticDomainsFE] = useState(project.useAutomaticDomains);
  const updateUseAutomaticMutation =
    projectsApiService.mutations.projects.useUpdateProjectUseAutomatic();
  const handleUpdateUseAutomatic = (useAutomatic: boolean) => {
    setUseAutomaticDomainsFE(useAutomatic);
    setDomainIds((prev) =>
      prev.filter((d) =>
        project.domains.some(
          (pd) => pd.id === d && (useAutomatic || pd.type !== DomainTypeGq.AUTOMATIC)
        )
      )
    );
    updateUseAutomaticMutation({
      id: project.id,
      useAutomaticDomains: useAutomatic,
    }).then(() => reloadKeywords());
  };

  // Sizing
  const { panelRef, width } = useWidthObserver();
  const dateError = !!moment.utc(dateSelectBag.dateMax).isBefore(dateSelectBag.dateMin);

  return (
    <div>
      <div className="py-4 flex flex-row justify-between">
        <div className={`flex flex-row items-center gap-6 `}>
          <VoxelToggleTabs
            menuListItems={menuItems}
            selectedId={selectedDashboard}
            setSelectedId={setSelectedDashboard}
          />
          <div className="flex flex-row flex-nowrap items-center gap-x-2">
            <VoxelDatePicker
              label="Period from"
              dateValue={dateSelectBag.dateMin}
              setDateValue={dateSelectBag.setDateMin}
              activeDates={dateSelectBag.relevantDates}
              error={dateError}
            />
            <VoxelDatePicker
              label="to"
              dateValue={dateSelectBag.dateMax}
              setDateValue={dateSelectBag.setDateMax}
              activeDates={dateSelectBag.relevantDates}
              error={dateError}
            />

            {dateError && (
              <VoxelText variant="form-caption" color="error">
                Start date is larger than end date
              </VoxelText>
            )}
          </div>
          {selectedDashboard === 'competitors' && (
            <VoxelAutocompleteDomainMulti
              domainIds={domainIds}
              setDomainIds={setDomainIds}
              project={project}
              label="Domains"
              labelPosition="left"
              disabled={!project.domains.length}
              inputWidthClass="w-64"
              size="sm"
              bottomElements={[
                <div className="w-full flex flex-row justify-between">
                  <VoxelText variant="form-caption">Include automatic domains</VoxelText>
                  <VoxelSwitch
                    size="sm"
                    value={useAutomaticDomainsFE}
                    onChange={() => handleUpdateUseAutomatic(!useAutomaticDomainsFE)}
                  />
                </div>,
              ]}
            />
          )}
          {selectedDashboard === 'tags' && (
            <div className="flex flex-row gap-4">
              <VoxelAutocompleteTagGroup
                labelPosition="left"
                tagGroupId={tagGroupId}
                setTagGroupId={setTagGroupId}
                project={project}
                disabled={!project.tagGroups.length}
                inputWidthClass="w-64"
                size="sm"
              />
              <VoxelAutocompleteTagMulti
                tagIds={tagIds}
                labelPosition="left"
                setTagIds={setTagIds}
                project={project}
                tagGroupId={tagGroupId}
                inputWidthClass="w-64"
                size="sm"
              />
            </div>
          )}
        </div>
        <div className="flex flex-row flex-nowrap items-center gap-x-4 mr-2">
          {/* Subscribe */}
          {!!project.serpTrackingOn && (
            <SubscribeButton user={user} project={project} reload={reloadKeywords} />
          )}
          <VoxelTooltipShort message={'Weekly tracking of unarchived keywords'}>
            <span>
              <RankTrackingSwitch project={project} reload={reloadKeywords} />
            </span>
          </VoxelTooltipShort>
        </div>
      </div>

      <div ref={panelRef} className="flex flex-row flex-nowrap">
        <>
          {selectedDashboard === 'site' && (
            <ProgressDashboardSite
              width={width}
              project={project}
              filter={filter}
              pollInterval={pollInterval}
              setDashboardLoading={setDashboardLoading}
            />
          )}
          {selectedDashboard === 'competitors' && (
            <ProgressDashboardCompetitors
              width={width}
              project={project}
              filter={filter}
              pollInterval={pollInterval}
              domainIds={domainIds}
              setDashboardLoading={setDashboardLoading}
            />
          )}
          {selectedDashboard === 'tags' && project.progressHasTags && (
            <ProgressDashboardTags
              width={width}
              project={project}
              filter={filter}
              pollInterval={pollInterval}
              tagGroup={tagGroup}
              tagGroupId={tagGroupId}
              tagIds={tagIds}
              setDashboardLoading={setDashboardLoading}
            />
          )}
        </>
      </div>
    </div>
  );
};

export default ProgressDashboard;
